import React, { useState } from 'react'
import './TestDualLogin.css'
import { getEnv } from '../config/env'

const INSIGHTS_ENDPOINTS = {
  'bot.dev.stratsys.net': 'https://data-ai-insights.svc.dev.stratsys.net',
  'bot.svc.dev.stratsys.net': 'https://data-ai-insights.svc.dev.stratsys.net',
  'bot.test.stratsys.net': 'https://data-ai-insights.svc.test.stratsys.net',
  'bot.stratsys.com': 'https://data-ai-insights.svc.stratsys.com',
  'localhost:8080': 'https://data-ai-insights.svc.dev.stratsys.net',
  'localhost:3000': 'http://localhost:5001'
}

const DEFAULT_TEXT =
  'The Eiffel Tower is a wrought-iron lattice tower on the Champ de Mars in Paris, France. ' +
  "It was constructed from 1887 to 1889 as the centerpiece of the 1889 World's Fair. " +
  'The tower is 330 metres tall and was the tallest man-made structure in the world until 1930.'

const DEFAULT_SUMMARY = "The Eiffel Tower is a 330m iron tower in Paris built for the 1889 World's Fair."

function TestSummarization({ authType }) {
  const isInternal = authType === 'internal'
  const isNoAuth = authType === 'none'
  const [text, setText] = useState(DEFAULT_TEXT)
  const [summary, setSummary] = useState(DEFAULT_SUMMARY)
  const [threshold, setThreshold] = useState('0.5')
  const [n, setN] = useState('5')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastRequest, setLastRequest] = useState(null)
  const [lastResponse, setLastResponse] = useState(null)
  const [responseError, setResponseError] = useState(false)

  const baseUrl = INSIGHTS_ENDPOINTS[window.location.host] || getEnv('REACT_APP_AI_INSIGHTS_BASE_URL')

  const sendSummarization = async () => {
    setLoading(true)
    setError(null)
    setLastResponse(null)
    setResponseError(false)

    const payload = {
      text: text,
      summary: summary,
      threshold: parseFloat(threshold),
      n: parseInt(n, 10)
    }

    const url = `${baseUrl}/evaluate/summarization`
    setLastRequest({ url, method: 'POST', body: payload, authType })

    try {
      let response
      if (isNoAuth) {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        response = { status: res.status, body: await res.json() }
      } else {
        response = await window.PDR.api.post(url, payload)
      }
      setLastResponse(response)
    } catch (e) {
      console.error('Summarization request failed:', e)
      setResponseError(true)
      setLastResponse(e.message || e.toString())
      setError('Request failed — see response panel for details.')
    } finally {
      setLoading(false)
    }
  }

  const authLabel = isNoAuth ? '⛔ No Auth' : isInternal ? '🔒 Internal Login' : '🔑 Normal Login'
  const bgClass = isNoAuth ? 'noauth-bg' : isInternal ? 'internal-bg' : 'normal-bg'
  const badgeClass = isNoAuth ? 'noauth-badge' : isInternal ? 'internal-badge' : 'normal-badge'
  const badgeText = isNoAuth
    ? 'No token (raw fetch)'
    : isInternal
      ? 'Internal (internallogin.svc)'
      : 'Normal (login.svc)'

  return (
    <div className="dual-login-container">
      <div className="header">
        <img
          className="stratsys-search-logo"
          src="https://frontend-packages.svc.stratsys.com/logos/icon-dark.svg"
          alt="Stratsys Icon"
        />
        <div className="one-ux-typography--heading-500">
          <one-ux-text>Test Summarization — {authLabel}</one-ux-text>
        </div>
      </div>

      <div className={`auth-section ${bgClass}`}>
        <span className={`auth-badge ${badgeClass}`}>{badgeText}</span>
        <span className="auth-status">
          {isNoAuth ? (
            'No Authorization header sent — expects 401'
          ) : (
            <>
              Token provided by App.js via{' '}
              <code>{isInternal ? 'REACT_APP_INTERNAL_LOGIN_URL' : 'REACT_APP_AUTH_URL'}</code>
            </>
          )}
        </span>
        <div className="switch-links">
          {!isNoAuth || isInternal ? (
            <a className="switch-link" href="/test_dual_login_normal">
              🔑 Normal
            </a>
          ) : null}
          {!isInternal ? (
            <a className="switch-link" href="/test_dual_login_internal">
              🔒 Internal
            </a>
          ) : null}
          {!isNoAuth ? (
            <a className="switch-link" href="/test_dual_login_noauth">
              ⛔ No Auth
            </a>
          ) : null}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Form */}
      <div className="form-section">
        <label>Source Text</label>
        <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} />

        <label>Summary</label>
        <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />

        <div className="form-row">
          <div>
            <label>Threshold (0–1)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </div>
          <div>
            <label>Assessment Questions (n)</label>
            <input type="number" min="1" max="20" value={n} onChange={(e) => setN(e.target.value)} />
          </div>
        </div>

        <button className="send-btn" onClick={sendSummarization} disabled={loading}>
          {loading ? 'Evaluating…' : '▶ Send Summarization'}
        </button>
      </div>

      {loading && <div className="loader"></div>}

      {/* Request / Response panels */}
      {(lastRequest || lastResponse) && (
        <div className="viz-panels">
          {lastRequest && (
            <div className="viz-panel request">
              <div className="viz-panel-header">📤 Request</div>
              <div className="viz-panel-body">
                <div className="viz-meta">
                  {lastRequest.method} {lastRequest.url}
                  <br />
                  Auth: <strong>{lastRequest.authType}</strong>
                </div>
                <pre>{JSON.stringify(lastRequest.body, null, 2)}</pre>
              </div>
            </div>
          )}

          {lastResponse !== null && (
            <div className={`viz-panel response ${responseError ? 'error' : ''}`}>
              <div className="viz-panel-header">{responseError ? '❌ Error Response' : '📥 Response'}</div>
              <div className="viz-panel-body">
                <pre>{typeof lastResponse === 'string' ? lastResponse : JSON.stringify(lastResponse, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const TestDualLoginNormal = () => <TestSummarization authType="normal" />
export const TestDualLoginInternal = () => <TestSummarization authType="internal" />
export const TestDualLoginNoAuth = () => <TestSummarization authType="none" />
